"use client";

import { useEffect, useState } from "react";

/**
 * Thin reading-progress bar pinned to the very top of the viewport.
 * Tracks the user's scroll position relative to the article's height
 * and fills left-to-right in the brand orange. Mirrors the indicator
 * Outrank's blog uses — gives the reader an at-a-glance sense of how
 * much of the article is left.
 *
 * Listens to scroll with `passive: true` so it never blocks input,
 * and uses requestAnimationFrame to coalesce updates — multiple scroll
 * events fired by the browser within one frame collapse to a single
 * setState, which keeps the bar smooth on high-refresh-rate displays
 * without re-rendering the React tree on every pixel.
 */
export default function BlogReadingProgress() {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        let rafId: number | null = null;

        function update() {
            rafId = null;
            const scrolled = window.scrollY;
            const max = document.documentElement.scrollHeight - window.innerHeight;
            const pct = max > 0 ? Math.min(100, Math.max(0, (scrolled / max) * 100)) : 0;
            setProgress(pct);
        }

        function onScroll() {
            if (rafId !== null) return;
            rafId = requestAnimationFrame(update);
        }

        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll, { passive: true });
        update();

        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            if (rafId !== null) cancelAnimationFrame(rafId);
        };
    }, []);

    return (
        <div
            className="blog-reading-progress"
            role="progressbar"
            aria-label="Article reading progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
        >
            <div className="blog-reading-progress-fill" style={{ width: `${progress}%` }} />
        </div>
    );
}
